import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, X, Coffee, Brain } from "lucide-react";
import Avatar from "./Avatar";

const PRESETS = [
    { id: 5,  label: "5 min",  break_: 1 },
    { id: 10, label: "10 min", break_: 2 },
    { id: 15, label: "15 min", break_: 3 },
    { id: 20, label: "20 min", break_: 4 },
    { id: 25, label: "25 min", break_: 5 },
    { id: 30, label: "30 min", break_: 5 },
];

/** Tiny start menu shown next to a reminder row. */
export function PomodoroStartMenu({ onStart, onClose, placement = "right" }) {
    const [customOpen, setCustomOpen] = useState(false);
    const [mins, setMins] = useState("25");
    const [secs, setSecs] = useState("00");
    const [breakMin, setBreakMin] = useState("5");

    const startCustom = () => {
        const m = Math.max(0, parseInt(mins || "0", 10) || 0);
        const s = Math.max(0, Math.min(59, parseInt(secs || "0", 10) || 0));
        const totalMin = m + s / 60;
        if (totalMin <= 0) return;
        const br = Math.max(0, parseInt(breakMin || "0", 10) || 0);
        onStart(totalMin, br);
        onClose();
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 4, scale: 0.95 }}
                transition={{ type: "spring", stiffness: 280, damping: 22 }}
                className={`absolute ${placement === "left" ? "left-0" : "right-0"} mt-2 bg-cream rounded-2xl shadow-float p-2 border border-warm-300/40 z-40 w-60`}
                onClick={(e) => e.stopPropagation()}
                data-testid="pomodoro-menu"
            >
                <p className="font-display text-[10px] uppercase tracking-widest text-warm-500 px-2 pt-1 pb-1.5">
                    Focus length
                </p>
                {PRESETS.map((p) => (
                    <button
                        key={p.id}
                        onClick={() => { onStart(p.id, p.break_); onClose(); }}
                        className="w-full text-left px-2 py-2 rounded-xl hover:bg-surface flex items-center gap-2 font-body text-sm text-warm-900 transition-all"
                        data-testid={`pomodoro-start-${p.id}`}
                    >
                        <Brain size={14} className="text-terracotta" />
                        <span className="flex-1">{p.label}</span>
                        <span className="text-[10px] text-warm-500 font-display uppercase">+{p.break_}m break</span>
                    </button>
                ))}

                <button
                    onClick={() => setCustomOpen((v) => !v)}
                    className={`w-full text-left px-2 py-2 rounded-xl hover:bg-surface flex items-center gap-2 font-body text-sm transition-all ${
                        customOpen ? "bg-surface text-warm-900" : "text-warm-800"
                    }`}
                    data-testid="pomodoro-start-custom"
                >
                    <Brain size={14} className="text-terracotta" />
                    <span className="flex-1">Custom…</span>
                    <span className="text-[10px] text-warm-500 font-display uppercase">m : s</span>
                </button>

                {customOpen && (
                    <div className="px-2 py-2" data-testid="pomodoro-custom-form">
                        <div className="flex items-center gap-1.5 mb-2">
                            <NumberInput value={mins} onChange={setMins} max={180} label="min" testId="pomo-custom-min" />
                            <span className="font-display text-warm-500 text-lg leading-none">:</span>
                            <NumberInput value={secs} onChange={setSecs} max={59} label="sec" pad testId="pomo-custom-sec" />
                            <span className="font-display text-[10px] uppercase tracking-widest text-warm-500 ml-auto">focus</span>
                        </div>
                        <div className="flex items-center gap-1.5 mb-2">
                            <NumberInput value={breakMin} onChange={setBreakMin} max={60} label="min" testId="pomo-custom-break" />
                            <span className="font-display text-[10px] uppercase tracking-widest text-warm-500 ml-auto">break</span>
                        </div>
                        <button
                            onClick={startCustom}
                            disabled={!(parseInt(mins || "0", 10) || parseInt(secs || "0", 10))}
                            className="w-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-40 text-cream font-display text-xs py-2 rounded-full transition-all active:scale-95"
                            data-testid="pomo-custom-go"
                        >
                            Start focus
                        </button>
                    </div>
                )}
            </motion.div>
        </AnimatePresence>
    );
}

function NumberInput({ value, onChange, max, label, pad, testId }) {
    const handle = (v) => {
        const digits = v.replace(/\D/g, "").slice(0, 3);
        onChange(digits);
    };
    const blur = () => {
        let n = parseInt(value || "0", 10) || 0;
        if (n > max) n = max;
        onChange(pad ? String(n).padStart(2, "0") : String(n));
    };
    return (
        <div className="bg-surface rounded-xl flex items-center px-2 py-1 flex-1">
            <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={value}
                onChange={(e) => handle(e.target.value)}
                onBlur={blur}
                className="bg-transparent outline-none font-display text-warm-900 text-lg w-10 text-center tabular-nums"
                data-testid={testId}
                aria-label={label}
            />
            <span className="font-body text-[10px] text-warm-500 uppercase tracking-widest">{label}</span>
        </div>
    );
}

/** Floating pomodoro banner pinned below the header. One active timer at a time. */
export function PomodoroTimer({
    session,    // { reminderId, title, focusMin, breakMin, startedAt, phase: 'focus'|'break', avatarStyle }
    onComplete, // (phase, session) => void  - called when focus or break ends
    onCancel,   // () => void
}) {
    const [remaining, setRemaining] = useState(0);
    const [paused, setPaused] = useState(false);
    const pausedAtRef = useRef(null);
    const pausedTotalRef = useRef(0);

    // Reset pause bookkeeping whenever a different session/phase starts.
    const sessionKey = session ? `${session.reminderId}:${session.phase}` : null;
    useEffect(() => {
        if (!sessionKey) return;
        pausedAtRef.current = null;
        pausedTotalRef.current = 0;
        setPaused(false);
    }, [sessionKey]);

    useEffect(() => {
        if (!session) return;
        const total = (session.phase === "focus" ? session.focusMin : session.breakMin) * 60;
        const compute = () => {
            const elapsed = Math.floor((Date.now() - session.startedAt - pausedTotalRef.current) / 1000);
            return Math.max(0, total - elapsed);
        };
        setRemaining(compute());
        const id = setInterval(() => {
            if (paused) return;
            const r = compute();
            setRemaining(r);
            if (r <= 0) {
                clearInterval(id);
                onComplete?.(session.phase, session);
            }
        }, 500);
        return () => clearInterval(id);
    }, [session, paused, onComplete]);

    if (!session) return null;

    const totalSec = (session.phase === "focus" ? session.focusMin : session.breakMin) * 60;
    const pct = totalSec ? Math.max(0, Math.min(100, (1 - remaining / totalSec) * 100)) : 0;
    const mm = Math.floor(remaining / 60);
    const ss = remaining % 60;
    const isBreak = session.phase === "break";

    const togglePause = () => {
        if (paused) {
            // resuming
            pausedTotalRef.current += Date.now() - pausedAtRef.current;
            pausedAtRef.current = null;
            setPaused(false);
        } else {
            pausedAtRef.current = Date.now();
            setPaused(true);
        }
    };

    return (
        <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -30, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            className={`relative overflow-hidden rounded-clay border-2 ${
                isBreak ? "border-sage bg-sage-soft/40" : "border-terracotta bg-terracotta-soft/30"
            } shadow-clay`}
            data-testid="pomodoro-banner"
        >
            {/* Progress bar background */}
            <div
                className={`absolute inset-y-0 left-0 ${isBreak ? "bg-sage/30" : "bg-terracotta/25"} transition-all`}
                style={{ width: `${pct}%` }}
            />
            <div className="relative flex items-center gap-3 px-4 py-3">
                <div className="shrink-0">
                    <Avatar mood={isBreak ? "cozy" : "energetic"} size={48} style={session.avatarStyle || "feminine"} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-display text-[10px] uppercase tracking-widest text-warm-500 inline-flex items-center gap-1">
                        {isBreak ? <Coffee size={10} /> : <Brain size={10} />}
                        {isBreak ? "Break time" : "Focus session"}
                    </p>
                    <p className="font-display text-warm-900 text-base leading-snug truncate" data-testid="pomodoro-title">
                        {session.title}
                    </p>
                </div>
                <div className="shrink-0 font-display text-warm-900 text-2xl tabular-nums" data-testid="pomodoro-clock">
                    {String(mm).padStart(2, "0")}:{String(ss).padStart(2, "0")}
                </div>
                <div className="shrink-0 flex items-center gap-1.5">
                    <button
                        onClick={togglePause}
                        className="w-9 h-9 rounded-full bg-cream hover:bg-surface flex items-center justify-center text-warm-800 transition-all active:scale-95"
                        data-testid="pomodoro-pause"
                        aria-label={paused ? "Resume" : "Pause"}
                        title={paused ? "Resume" : "Pause"}
                    >
                        {paused ? <Play size={14} /> : <Pause size={14} />}
                    </button>
                    <button
                        onClick={onCancel}
                        className="w-9 h-9 rounded-full bg-cream hover:bg-surface flex items-center justify-center text-warm-800 transition-all active:scale-95"
                        data-testid="pomodoro-cancel"
                        aria-label="Cancel"
                        title="Cancel session"
                    >
                        <X size={14} />
                    </button>
                </div>
            </div>
        </motion.div>
    );
}
